/**
 * V2 PaSurveyPausePage
 * Survey pause/timeout page with Athena integration
 */

import { memo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import { Enum_Survey_Access_Restriction, notEmpty } from '@cyren/common-lib';
import { Button } from '@mui/material';
import { useStaticLocales, SystemLocaleKeys } from '../../../../admin/hooks/locales/use-static-locale';
import { usePatientLocale } from '../../../hooks/use-patient-locale';
import Footer from '../../../../app/Footer';
import LoadingAnim from '../../../../components/LoadingAnim';
import LogoPatientDynamic from '../../../../components/LogoPatientDynamic';
import PaTreeHeader from './components/PaTreeHeader';
import SurveyContainer from './SurveyContainer';
import thumbsUp from '../../../../assets/ill/Thumbs_up.svg';

const collapCls =
  'collapse collapse-arrow border-2 border-black border-opacity-10 rounded-none first:rounded-t-xl last:rounded-b-xl border-t-0 first:border-t-2 font-base';
const collapHeaderCls = 'collapse-title text-base font-medium';
const collapBodyCls = 'collapse-content opacity-70 text-base';

/**
 * Page Content Component
 */
function PaSurveyPausePageContent() {
  const nav = useNavigate();
  const { localeCode } = usePatientLocale();
  const { sltStr } = useStaticLocales({ localeCode });

  // Get all data from Container
  const {
    isLoading,
    surveyState,
    survey,
    visit,
    org,
    navigation,
    athena,
    descriptiveNote,
  } = SurveyContainer.useContainer();

  const { baseUrl } = navigation;
  const {
    isProcessing: isAthenaProcessing,
    sendDescriptiveNoteToAthena,
    handleAllergy,
    handleMedication,
    handleProviderAndPharmacy,
  } = athena;
  const { isGenerating, generateDescriptiveNote, descriptiveReport } = descriptiveNote;

  // Local state
  const [selectedFaqIndex, setSelectedFaqIndex] = useState<number | null>(null);
  const [athenaIntegrationExecuted, setAthenaIntegrationExecuted] = useState(false);
  const [descriptiveNoteGenerated, setDescriptiveNoteGenerated] = useState(false);

  // Get encounterCheckReason from surveyState
  const { encounterCheckReason, encounterId } = surveyState;

  // FAQ data
  const faqItems: Array<{ id: string; questionKey: SystemLocaleKeys; answerKey: SystemLocaleKeys }> = [];

  // Generate descriptive note when page loads
  useEffect(() => {
    if (!descriptiveNoteGenerated && !isGenerating && !descriptiveReport) {
      generateDescriptiveNote();
      setDescriptiveNoteGenerated(true);
    }
  }, [descriptiveNoteGenerated, isGenerating, descriptiveReport, generateDescriptiveNote]);

  // Execute Athena integration
  useEffect(() => {
    if (surveyState.patientDetails && !athenaIntegrationExecuted && descriptiveReport && encounterId) {
      const executeAthenaIntegration = async () => {
        try {
          // Clean up localStorage
          localStorage.removeItem('encounterCheck');

          // Send descriptive note to Athena
          await sendDescriptiveNoteToAthena(encounterId, descriptiveReport);

          // Handle allergy updates
          await handleAllergy();

          // Handle medication updates
          await handleMedication();

          // Handle provider and pharmacy
          await handleProviderAndPharmacy();

          // Navigate to terminated page
          nav(`${baseUrl}/terminated`);
        } catch (error) {
          console.error('Athena integration error:', error);
          nav(`${baseUrl}/terminated`);
        } finally {
          setAthenaIntegrationExecuted(true);
        }
      };

      executeAthenaIntegration();
    }
  }, [
    surveyState.patientDetails,
    athenaIntegrationExecuted,
    descriptiveReport,
    encounterId,
    sendDescriptiveNoteToAthena,
    handleAllergy,
    handleMedication,
    handleProviderAndPharmacy,
    nav,
    baseUrl,
  ]);

  // Loading state
  if (isLoading || isAthenaProcessing || isGenerating) {
    return (
      <div className='flex flex-1 w-full'>
        <LoadingAnim className='h-full p-10 w-full' />
      </div>
    );
  }

  // Check if provider report should be accessible
  const canViewProviderReport =
    survey?.access_restriction &&
    survey.access_restriction !== Enum_Survey_Access_Restriction.ProviderOnly;

  // Render message based on encounterCheckReason (matching V1)
  const renderMessage = () => {
    if (!visit?.archived) {
      return (
        <div className='flex-center flex-col gap-4'>
          <div className='pt-12 pb-6 w-1/3'>
            <img src={thumbsUp} alt='Thumbs Up' />
          </div>

          <div className='flex-col gap-2 flex-center-y'>
            {(() => {
              if (encounterCheckReason === 'with_staff') {
                return (
                  <>
                    <h4 className='font-bold sm'>Your care team is ready!</h4>
                    <div className='text-base opacity-70 px-2 text-center'>
                      Your information has been sent to your documents because your care team is ready to see you.
                    </div>
                  </>
                );
              }
              if (encounterCheckReason === 'timeout') {
                return (
                  <>
                    <h4 className='font-bold sm'>Thank You!</h4>
                    <div className='text-base opacity-70 px-2 text-center'>
                      You&apos;ve reached the 30 minute limit. The information you have submitted so far will be sent to your documents.
                    </div>
                  </>
                );
              }
              return (
                <>
                  <h4 className='font-bold sm'>Thank You!</h4>
                  <div className='text-base opacity-70 px-2 text-center'>
                    Your information has been sent to your care team because your visit has started.
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      );
    }

    // Archived message
    return (
      <div className='flex-center flex-col gap-4 py-10'>
        <div className='flex-col gap-2 flex-center-y'>
          <div className='text-base opacity-70 px-2 text-center flex-1 bg-primary bg-opacity-10 rounded-xl w-full p-2'>
            {sltStr({ key: 'finished-archived' })}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className='flex-1 flex-col gap-5 justify-between'>
      <div className='flex-1 flex-col gap-5'>
        <PaTreeHeader showFullBar noBack logoUrl={org?.logoUrl} />

        <div className='px-5'>
          <div className='flex-center'>
            <LogoPatientDynamic logoUrl={org?.logoUrl} className='h-6 w-auto' />
          </div>

          {renderMessage()}

          {/* View Provider Report Button */}
          {canViewProviderReport && (
            <div className='flex-center flex-col gap-4 py-10'>
              <div className='flex-col gap-2 flex-center-y'>
                <Button
                  variant='contained'
                  onClick={() => nav(`/sp/${visit?.uuid}`)}
                >
                  View Medical Provider Report
                </Button>
              </div>
            </div>
          )}

          {/* FAQ Section */}
          {notEmpty(faqItems) && (
            <div className='flex-col py-4 w-full'>
              {faqItems.map((item, idx) => {
                const open = selectedFaqIndex === idx;
                return (
                  <div
                    key={item.id}
                    className={classNames(collapCls, open && 'collapse-open')}
                    onClick={() => setSelectedFaqIndex(open ? null : idx)}
                  >
                    <div className={collapHeaderCls}>
                      {sltStr({ key: item.questionKey })}
                    </div>
                    <div className={collapBodyCls}>
                      <p>{sltStr({ key: item.answerKey })}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className='hidden flex-none'>
        <Footer />
      </div>
    </div>
  );
}

/**
 * Main Page Component
 */
function PaSurveyPausePage() {
  return (
    <SurveyContainer.Provider>
      <PaSurveyPausePageContent />
    </SurveyContainer.Provider>
  );
}

export default memo(PaSurveyPausePage);
